import axios from "axios";
import { createContext, useState } from "react";
import { toast } from "react-toastify";

export const courseContext = createContext();

export const CourseContextProvider = (props) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [createdCourses, setCreatedCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [loading, setLoading] = useState(false);

  const getAllCourses = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(backendUrl + "/api/courses");
      if (data.success) {
        setCourses(data.courses);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getEnrolledCourses = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(backendUrl + "/api/courses/enrolled");
      if (data.success) {
        setEnrolledCourses(data.courses);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCreatedCourses = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(backendUrl + "/api/courses/created");
      if (data.success) {
        setCreatedCourses(data.courses);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCourseById = async (courseId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(backendUrl + `/api/courses/${courseId}`);
      if (data.success) {
        setCurrentCourse(data.course);
        return data.course;
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createCourse = async (courseData) => {
    try {
      setLoading(true);
      const { data } = await axios.post(backendUrl + "/api/courses", courseData);
      if (data.success) {
        setCreatedCourses(prev => [data.course, ...prev]);
        toast.success("Course created successfully!");
        return { success: true, course: data.course };
      } else {
        toast.error(data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create course");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCourse = async (courseId, courseData) => {
    try {
      setLoading(true);
      const { data } = await axios.put(backendUrl + `/api/courses/${courseId}`, courseData);
      if (data.success) {
        setCreatedCourses(prev => 
          prev.map(course => course._id === courseId ? data.course : course)
        );
        setCourses(prev => 
          prev.map(course => course._id === courseId ? data.course : course)
        );
        toast.success("Course updated successfully!");
        return { success: true, course: data.course };
      } else {
        toast.error(data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update course");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteCourse = async (courseId) => {
    try {
      setLoading(true);
      const { data } = await axios.delete(backendUrl + `/api/courses/${courseId}`);
      if (data.success) {
        setCreatedCourses(prev => prev.filter(course => course._id !== courseId));
        setCourses(prev => prev.filter(course => course._id !== courseId));
        toast.success("Course deleted successfully!");
        return { success: true };
      } else {
        toast.error(data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete course");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const enrollInCourse = async (courseId) => {
    try {
      setLoading(true);
      const { data } = await axios.post(backendUrl + `/api/courses/${courseId}/enroll`);
      if (data.success) {
        setEnrolledCourses(prev => [data.course, ...prev]);
        toast.success("Successfully enrolled in course!");
        return { success: true };
      } else {
        toast.error(data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to enroll in course");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const unenrollFromCourse = async (courseId) => {
    try {
      setLoading(true);
      const { data } = await axios.delete(backendUrl + `/api/courses/${courseId}/enroll`);
      if (data.success) {
        setEnrolledCourses(prev => prev.filter(course => course._id !== courseId));
        toast.success("Successfully unenrolled from course!");
        return { success: true };
      } else {
        toast.error(data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unenroll from course");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const isEnrolled = (courseId) => {
    return enrolledCourses.some(course => course._id === courseId);
  };

  const isCreator = (courseId) => {
    return createdCourses.some(course => course._id === courseId);
  };

  const value = {
    backendUrl,
    courses,
    setCourses,
    enrolledCourses,
    setEnrolledCourses,
    createdCourses,
    setCreatedCourses,
    currentCourse,
    setCurrentCourse,
    loading,
    getAllCourses,
    getEnrolledCourses,
    getCreatedCourses,
    getCourseById,
    createCourse,
    updateCourse,
    deleteCourse,
    enrollInCourse,
    unenrollFromCourse,
    isEnrolled,
    isCreator,
  };

  return (
    <courseContext.Provider value={value}>
      {props.children}
    </courseContext.Provider>
  );
};